import { useCallback, useState } from 'react';
import { type KeyedMutator, useSWRConfig } from 'swr';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components';
import { AppRoutes } from '@/constants/routes';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import { type ReportWithEvents } from '@/types/report';

interface UseReportActionsResult {
    editModalOpen: boolean;
    openEditModal: () => void;
    closeEditModal: () => void;
    submitEdit: (
        name: string,
        description: string | undefined,
    ) => Promise<void>;
    deleteModalOpen: boolean;
    openDeleteModal: () => void;
    closeDeleteModal: () => void;
    deleteInProgress: boolean;
    confirmDelete: () => Promise<void>;
}

export function useReportActions(
    reportId: string,
    mutate: KeyedMutator<ReportWithEvents>,
): UseReportActionsResult {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const navigate = useNavigate();
    const { mutate: mutateCache } = useSWRConfig();
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteInProgress, setDeleteInProgress] = useState(false);

    const submitEdit = useCallback(
        async (name: string, description: string | undefined) => {
            try {
                await EngineService.updateReport(reportId, {
                    name,
                    description,
                });
                toast.success(t('updateSuccess'));
                await mutate();
                await mutateCache(
                    (key) =>
                        Array.isArray(key) &&
                        key[0] === CACHE_KEYS.REPORTS_LIST,
                    undefined,
                    { revalidate: true },
                );
            } catch (submitError) {
                toast.error(t('updateError'));
                throw submitError;
            }
        },
        [reportId, mutate, mutateCache, t],
    );

    const confirmDelete = useCallback(async () => {
        if (deleteInProgress) {
            return;
        }
        setDeleteInProgress(true);
        try {
            await EngineService.deleteReport(reportId);
            toast.success(t('deleteSuccess'));
            setDeleteModalOpen(false);
            await mutateCache(
                (key) =>
                    Array.isArray(key) && key[0] === CACHE_KEYS.REPORTS_LIST,
                undefined,
                { revalidate: true },
            );
            navigate(AppRoutes.REPORTS);
        } catch {
            toast.error(t('deleteError'));
        } finally {
            setDeleteInProgress(false);
        }
    }, [deleteInProgress, reportId, mutateCache, navigate, t]);

    return {
        editModalOpen,
        openEditModal: () => setEditModalOpen(true),
        closeEditModal: () => setEditModalOpen(false),
        submitEdit,
        deleteModalOpen,
        openDeleteModal: () => setDeleteModalOpen(true),
        closeDeleteModal: () => setDeleteModalOpen(false),
        deleteInProgress,
        confirmDelete,
    };
}
